import React, { useCallback, useEffect, useState } from "react";
import { UserPlus, Trash2, Copy, Users, ExternalLink, Inbox, MessageSquare, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

function ClientActivity({ clientId }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/client-activity`); setData(data); }
    catch { /* silent */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const markSeen = async () => {
    try { await api.post(`/admin/enterprise360/clients/${clientId}/client-activity/seen`); load(); toast.success("Marked as read"); }
    catch { toast.error("Failed"); }
  };
  if (!data) return null;
  const items = data.items || [];
  return (
    <div data-testid="client-activity" className="border border-zinc-200 rounded-lg p-3 mb-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[12px] tracking-[0.12em] uppercase text-zinc-500 inline-flex items-center gap-1.5">
          <Inbox size={14} /> Client activity
          {data.unseen > 0 && <span data-testid="activity-unseen-badge" className="bg-red-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{data.unseen} new</span>}
        </span>
        {data.unseen > 0 && <button data-testid="activity-mark-seen" onClick={markSeen} className="text-[11px] tracking-[0.1em] uppercase text-zinc-500 hover:text-black">Mark all read</button>}
      </div>
      {items.length === 0 ? <p className="text-[12px] text-zinc-400 py-2">No client submissions yet — risks raised and decision responses from the portal appear here.</p>
        : <div className="divide-y divide-zinc-100 max-h-64 overflow-auto" data-testid="activity-list">
            {items.map((a, i) => {
              const unseen = !data.seen_at || (a.at || "") > data.seen_at;
              return (
                <div key={i} data-testid="activity-item" className={`py-2 flex items-start gap-2 ${unseen ? "" : "opacity-60"}`}>
                  {a.kind === "raid" ? <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-none" /> : <MessageSquare size={14} className="text-blue-500 mt-0.5 flex-none" />}
                  <div className="flex-1">
                    <div className="text-[12px] text-zinc-800">
                      {a.kind === "raid" ? <>Raised a {a.meta?.toLowerCase() || "risk"}: </> : <>Responded on <span className="font-mono text-zinc-400">{a.ref}</span> ({a.meta}): </>}
                      <span className="text-zinc-600">{a.text}</span>
                    </div>
                    <div className="text-[10px] text-zinc-400">{a.by} · {a.at ? new Date(a.at).toLocaleString("en-GB") : ""}</div>
                  </div>
                  {unseen && <span className="w-2 h-2 rounded-full bg-red-500 mt-1.5 flex-none" />}
                </div>);
            })}
          </div>}
    </div>
  );
}

export default function PortalAccessManager({ clientId }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ email: "", name: "" });

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/portal/grants`); setGrants(data.grants || []); }
    catch { toast.error("Could not load portal access"); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!f.email.trim()) { toast.error("Enter an email"); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/enterprise360/clients/${clientId}/portal/grants`, f); setGrants(data.grants); setF({ email: "", name: "" }); toast.success("Portal access granted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const revoke = async (g) => {
    if (!window.confirm(`Revoke portal access for ${g.email}?`)) return;
    setBusy(true);
    try { const { data } = await api.delete(`/admin/enterprise360/clients/${clientId}/portal/grants/${g.id}`); setGrants(data.grants); toast.success("Access revoked"); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  const portalUrl = `${BASE}/portal`;
  const active = grants.filter((g) => g.active);

  return (
    <div data-testid="portal-access-manager">
      <ClientActivity clientId={clientId} />
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-[12px] text-zinc-500 max-w-xl">Grant client stakeholders read-only access to this engagement's live hub. They sign in with a passwordless email magic-link — no password, scoped to this engagement only.</p>
        <a href={portalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-3 py-1.5 rounded" data-testid="portal-open-link">
          <ExternalLink size={13} /> Open portal
        </a>
      </div>

      <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 mb-4 flex-wrap" data-testid="portal-url-row">
        <Users size={14} className="text-zinc-400" />
        <span className="text-[12px] text-zinc-600 flex-1 min-w-[180px] font-mono">{portalUrl}</span>
        <button onClick={() => { navigator.clipboard?.writeText(portalUrl); toast.success("Portal link copied"); }}
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-zinc-500 hover:text-black" data-testid="portal-copy-link"><Copy size={12} /> Copy</button>
      </div>

      <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 mb-4">
        <div className="grid sm:grid-cols-2 gap-2 mb-2">
          <input data-testid="portal-grant-email" className="border border-zinc-300 rounded px-2 py-1.5 text-[13px] bg-white" placeholder="client.email@company.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <input data-testid="portal-grant-name" className="border border-zinc-300 rounded px-2 py-1.5 text-[13px] bg-white" placeholder="Name (optional)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </div>
        <button data-testid="portal-grant-submit" onClick={add} disabled={busy}
          className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.12em] uppercase bg-[var(--e360-primary)] text-white px-4 py-1.5 rounded disabled:opacity-40"><UserPlus size={14} /> Grant access</button>
      </div>

      {loading ? <p className="text-sm text-zinc-400 py-4 text-center">Loading…</p>
        : active.length === 0 ? <p className="text-sm text-zinc-500 py-4 text-center">No client users have portal access yet.</p>
        : <div className="border border-zinc-200 rounded-lg divide-y" data-testid="portal-grant-list">
            {active.map((g) => (
              <div key={g.id} data-testid={`portal-grant-${g.id}`} className="flex items-center justify-between px-3 py-2.5">
                <div><div className="text-[13px] text-zinc-800">{g.email}</div>
                  {g.name && <div className="text-[11px] text-zinc-400">{g.name}</div>}</div>
                <button data-testid={`portal-revoke-${g.id}`} onClick={() => revoke(g)} disabled={busy} className="text-zinc-300 hover:text-red-600 p-1" title="Revoke access"><Trash2 size={14} /></button>
              </div>))}
          </div>}
    </div>
  );
}
